import React, { useEffect, useMemo, useRef, useState } from "react";
import logo from "../assets/mhc-logo.png";
import verticalImg from "../assets/flight_simulator_training.png";
import { motion, useMotionValue, useAnimationFrame } from "framer-motion";
import AviationEcosystemSection from "../Components/AviationEcosystemSection";
import ImpactStatsGallerySection from "../Components/ImpactStatsGallerySection";
import DiversifiedCommunityText from "../Components/DiversifiedCommunityText";

export default function HeroSection() {
  const heroBg =
    "https://mhcglobal.info/Media/Home/airport.webp";

  const missionVisionBg =
    "https://mhcglobal.info/Media/Home/wing.webp";

  const recruiters = useMemo(
    () => [
      {
        img: "https://mhcglobal.info/Media/Logo/vels-university-logo.webp",
        alt: "Vels University",
        imgTitle: "Vels University",
      },
      {
        img: "https://mhcglobal.info/Media/Logo/bharath-university-logo.webp",
        alt: "Bharath University",
        imgTitle: "Bharath University",
      },
      {
        img: "https://mhcglobal.info/Media/Logo/crescent-institute-of-science-and-technology-logo.webp",
        alt: "Crescent University",
        imgTitle: "Crescent University",
      },
      {
        img: "https://mhcglobal.info/Media/Logo/jamal-mohamed-college.webp",
        alt: "Jamal Mohamed College",
        imgTitle: "Jamal Mohamed College",
      },
      {
        img: "https://mhcglobal.info/Media/Logo/bishop-heber-college-logo.webp",
        alt: "Bishop Heber College",
        imgTitle: "Bishop Heber College",
      },
      {
        img: "https://mhcglobal.info/Media/Logo/kalasalingam-academy-of-research-and-education.webp",
        alt: "Kalasalingam University",
        imgTitle: "Kalasalingam University",
      },
      {
        img: "https://mhcglobal.info/Media/Logo/sasurie-college-of-engineering-logo.png",
        alt: "Sasurie College",
        imgTitle: "Sasurie College",
      },
      {
        img: "https://mhcglobal.info/Media/Logo/alliance-university-alliance logo.webp",
        alt: "Alliance University",
        imgTitle: "Alliance University",
      },
      {
        img: "https://mhcglobal.info/Media/Logo/sanskriti-university-logo.webp",
        alt: "Sanskriti University",
        imgTitle: "Sanskriti University",
      },
    ],
    []
  );

  const joinLink = "https://mhcockpit.com/";

  const x = useMotionValue(0);
  const trackRef = useRef(null);
  const [trackWidth, setTrackWidth] = useState(0);
  const SPEED = 70;

  useEffect(() => {
    const measure = () => {
      if (!trackRef.current) return;
      const full = trackRef.current.scrollWidth;
      setTrackWidth(full / 2);
    };

    const raf = requestAnimationFrame(measure);
    window.addEventListener("resize", measure);

    const imgs = trackRef.current?.querySelectorAll("img") || [];
    imgs.forEach((img) => img.addEventListener("load", measure));

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", measure);
      imgs.forEach((img) => img.removeEventListener("load", measure));
    };
  }, []);

  useAnimationFrame((t, delta) => {
    if (!trackWidth) return;
    const moveBy = (SPEED * delta) / 1000;
    let current = x.get() - moveBy;
    if (current <= -trackWidth) current += trackWidth;
    x.set(current);
  });

  const verticalItems = useMemo(
    () => [
      {
        no: "01",
        title: "Pilot Training",
        desc: "Comprehensive flight training designed to build confident, skilled aviators. From ground school to cockpit mastery, we prepare you for a successful flying career.",
      },
      {
        no: "02",
        title: "Undergraduate Programs (UG)",
        desc: "A strong foundation in aviation management, operations, and industry fundamentals. Designed to equip students with practical knowledge and global career opportunities.",
      },
      {
        no: "03",
        title: "Postgraduate Programs (PG)",
        desc: "Advanced aviation education focused on leadership, strategy, and industry specialization. Ideal for graduates aiming to accelerate their careers in the aviation sector.",
      },
      {
        no: "04",
        title: "Certification Programs",
        desc: "Short-term, skill-focused programs tailored to industry demands. Perfect for upgrading expertise and gaining job-ready aviation certifications.",
      },
    ],
    []
  );

  const vContainer = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.18,
        delayChildren: 0.15,
      },
    },
  };

  const vRow = {
    hidden: { opacity: 0, y: 18 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, ease: "easeOut" },
    },
  };

  const vImageWrap = {
    hidden: { opacity: 0, scale: 1.02 },
    show: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.7, ease: "easeOut" },
    },
  };

  const vImageZoom = {
    hidden: { scale: 1.06 },
    show: {
      scale: 1,
      transition: { duration: 1.1, ease: "easeOut" },
    },
  };

  const scrollToBottom = () => {
  window.scrollTo({
    top: document.body.scrollHeight,
    behavior: "smooth",
  });
};

  return (
    <section className="bg-white overflow-x-hidden">
      <div className="overflow-hidden shadow-[0_30px_90px_rgba(0,0,0,0.2)]">
        <div className="px-1 py-1">
          {/* NAVBAR */}
          <div className="bg-black rounded-t-xl h-[68px] sm:h-[78px] md:h-[84px] lg:h-[90px] flex items-center justify-center px-4">
            <img
              src={logo}
              alt="MHC Global"
              className="h-[28px] sm:h-[34px] md:h-[36px] lg:h-[40px] object-contain"
            />
          </div>

          {/* HERO */}
          <div
            className="relative min-h-[460px] sm:min-h-[540px] md:min-h-[620px] lg:min-h-screen bg-cover bg-center flex flex-col justify-end items-center px-4 sm:px-6 pb-10 sm:pb-14 md:pb-16 lg:pb-20 rounded-b-xl"
            style={{ backgroundImage: `url(${heroBg})` }}
          >
            <div className="absolute inset-0 bg-black/40 rounded-b-xl" />

            <motion.div
              className="relative text-center w-full max-w-[1150px]"
              initial={{ opacity: 0, y: 80 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, ease: "easeOut" }}
            >
              <motion.h1
                className="text-white text-5xl max-sm:text-3xl font-semibold px-36 max-sm:px-5"
                initial={{ opacity: 0, y: 60 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 1 }}
              >
                We Building Structured Pathways for
                Education, Training, and Global Careers
              </motion.h1>

              <motion.button
               onClick={scrollToBottom}
                className="mt-5 sm:mt-6 md:mt-8 bg-[#1B86C8] text-white px-5 sm:px-6 md:px-7 py-3 sm:py-3.5 rounded-lg hover:brightness-110 active:scale-[0.98] transition shadow-[0_18px_45px_rgba(27,134,200,0.35)] text-[14px] sm:text-[15px] md:text-[16px] font-medium"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.8 }}
              >
                Explore More
              </motion.button>
            </motion.div>
          </div>
        </div>

        {/* PARTNERS MARQUEE */}
        <div className="bg-white px-4 sm:px-6 md:px-8 lg:px-10 py-8 sm:py-10 md:py-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex justify-center mb-6 sm:mb-8 md:mb-10">
              <h2 className="text-[22px] sm:text-[26px] md:text-[29px] font-semibold text-black text-center">
                Our Valuable Partners
              </h2>
            </div>

            <div
              className="relative overflow-hidden"
              style={{
                WebkitMaskImage:
                  "linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)",
                maskImage:
                  "linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)",
              }}
            >
              <motion.div style={{ x }} className="flex will-change-transform">
                <div ref={trackRef} className="flex">
                  {[...recruiters, ...recruiters].map((r, index) => (
                    <div
                      key={`${r.alt}-${index}`}
                      className="w-[160px] sm:w-[190px] md:w-[220px] lg:w-[260px] xl:w-[280px] h-[90px] sm:h-[105px] md:h-[120px] lg:h-[140px] xl:h-[150px] flex items-center justify-center flex-shrink-0 px-4 sm:px-5 md:px-6 lg:px-8"
                    >
                      <img
                        src={r.img}
                        alt={r.alt}
                        title={r.imgTitle}
                        className="max-w-full max-h-full object-contain"
                        draggable="false"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        <AviationEcosystemSection />

        {/* MISSION & VISION SECTION */}
        <div className="bg-white px-4 sm:px-6 md:px-8 lg:px-10 pb-12 sm:pb-14 md:pb-16">
          <div className="relative overflow-hidden rounded-[22px] sm:rounded-[28px] min-h-[520px]">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${missionVisionBg})` }}
            />
            <div className="absolute inset-0 bg-black/50" />
            <div className="absolute inset-y-0 left-0 w-[120px] sm:w-[180px] md:w-[220px] lg:w-[280px] bg-gradient-to-r from-black/70 to-transparent" />

            <div className="relative z-10 min-h-[520px] flex items-center">
              <div className="w-full px-4 sm:px-6 md:px-8 lg:px-14 py-10 sm:py-12">
                <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 md:gap-10 items-center">
                  {/* LEFT TEXT */}
                  <motion.div
                    className="xl:col-span-5"
                    initial={{ opacity: 0, x: -70 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.9, ease: "easeOut" }}
                  >
                    <h2 className="text-white font-bold leading-[1.1] text-[28px] sm:text-[36px] md:text-[42px] lg:text-[48px] max-w-[720px]">
                      Driven by Purpose. Guided
                      <br />
                      by Vision. Built for the Future of Aviation.
                    </h2>
                  </motion.div>

                  {/* RIGHT CARDS */}
                  <motion.div
                    className="xl:col-span-7"
                    initial={{ opacity: 0, x: 70, y: 40 }}
                    whileInView={{ opacity: 1, x: 0, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.9, ease: "easeOut", delay: 0.1 }}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 justify-center xl:justify-end">
                      {/* MISSION CARD */}
                      <motion.div
                        className="relative w-full rounded-[20px] sm:rounded-[22px] bg-white shadow-[0_22px_65px_rgba(0,0,0,0.25)] p-6 sm:p-7 md:p-8 min-h-[300px]"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ delay: 0.2, duration: 0.7 }}
                      >
                        <div className="absolute right-5 sm:right-6 top-5 text-[#2D9CDB]">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="42"
                            height="42"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M16 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2 1 1 0 0 1 1 1v1a2 2 0 0 1-2 2 1 1 0 0 0-1 1v2a1 1 0 0 0 1 1 6 6 0 0 0 6-6V5a2 2 0 0 0-2-2z" />
                            <path d="M5 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2 1 1 0 0 1 1 1v1a2 2 0 0 1-2 2 1 1 0 0 0-1 1v2a1 1 0 0 0 1 1 6 6 0 0 0 6-6V5a2 2 0 0 0-2-2z" />
                          </svg>
                        </div>

                        <div className="pt-10 sm:pt-12">
                          <h3 className="text-[20px] sm:text-[22px] font-extrabold text-black">
                            OUR
                          </h3>

                          <h3 className="mt-2 text-[30px] sm:text-[34px] font-extrabold text-[#2D9CDB] leading-none">
                            MISSION
                          </h3>

                          <p className="mt-4 sm:mt-5 text-[14px] sm:text-[15px] leading-[1.5] text-black/90">
                            To provide a transformative learning environment
                            through industry-integrated curriculum, cutting-edge
                            research, and mentorship from aviation professionals.
                          </p>
                        </div>
                      </motion.div>

                      {/* VISION CARD */}
                      <motion.div
                        className="relative w-full rounded-[20px] sm:rounded-[22px] bg-white shadow-[0_22px_65px_rgba(0,0,0,0.25)] p-6 sm:p-7 md:p-8 min-h-[300px]"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ delay: 0.35, duration: 0.7 }}
                      >
                        <div className="absolute right-5 sm:right-6 top-5 text-[#2D9CDB]">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="42"
                            height="42"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M16 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2 1 1 0 0 1 1 1v1a2 2 0 0 1-2 2 1 1 0 0 0-1 1v2a1 1 0 0 0 1 1 6 6 0 0 0 6-6V5a2 2 0 0 0-2-2z" />
                            <path d="M5 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2 1 1 0 0 1 1 1v1a2 2 0 0 1-2 2 1 1 0 0 0-1 1v2a1 1 0 0 0 1 1 6 6 0 0 0 6-6V5a2 2 0 0 0-2-2z" />
                          </svg>
                        </div>

                        <div className="pt-10 sm:pt-12">
                          <h3 className="text-[20px] sm:text-[22px] font-extrabold text-black">
                            OUR
                          </h3>

                          <h3 className="mt-2 text-[30px] sm:text-[34px] font-extrabold text-[#2D9CDB] leading-none">
                            VISION
                          </h3>

                          <p className="mt-4 sm:mt-5 text-[14px] sm:text-[15px] leading-[1.5] text-black/90">
                            To be a premier global center for excellence in
                            aeronautical education, fostering innovative thinkers
                            and ethical leaders who redefine the future of the
                            aviation industry.
                          </p>
                        </div>
                      </motion.div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* VERTICALS SECTION */}
        <section className="bg-white py-12 sm:py-16 lg:py-20">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-8">
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-10 xl:gap-12 items-center">
              {/* LEFT CONTENT */}
              <div className="xl:col-span-6">
                <motion.h3
                  className="text-[24px] sm:text-[30px] md:text-[34px] lg:text-[36px] font-bold leading-[1.18] text-black max-w-[680px] mx-auto text-center xl:text-left xl:mx-0"
                  initial={{ opacity: 0, y: 25 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                >
                  MHC Global operates across multiple education and training
                  verticals, each designed with a clear academic and career
                  outcome.
                </motion.h3>

                <motion.div
                  variants={vContainer}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, amount: 0.25 }}
                  className="mt-8 sm:mt-10 space-y-8 sm:space-y-10"
                >
                  {verticalItems.map((it) => (
                    <motion.div
                      key={it.no}
                      variants={vRow}
                      className="flex flex-col sm:flex-row items-center justify-center xl:justify-start gap-4 sm:gap-5 md:gap-6 text-center sm:text-left"
                    >
                      <div className="w-[48px] h-[40px] sm:w-[52px] sm:h-[42px] rounded-[10px] bg-[#F1F3F6] flex items-center justify-center flex-shrink-0">
                        <span className="text-[15px] sm:text-[16px] font-semibold text-black/80">
                          {it.no}
                        </span>
                      </div>

                      <div className="flex flex-col items-center sm:items-start xl:items-start">
                        <h4 className="text-[18px] sm:text-[20px] md:text-[22px] font-bold text-black leading-tight text-center sm:text-left xl:text-left">
                          {it.title}
                        </h4>
                        <p className="mt-2 text-[14px] sm:text-[15px] leading-[1.6] text-black/55 max-w-[560px] text-center sm:text-left xl:text-left">
                          {it.desc}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </div>

              {/* RIGHT IMAGE */}
              <motion.div
                variants={vImageWrap}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.25 }}
                className="xl:col-span-6"
              >
                <div className="flex justify-center xl:justify-end">
                  <div className="w-full max-w-[760px] rounded-[22px] sm:rounded-[26px] overflow-hidden shadow-[0_30px_90px_rgba(0,0,0,0.18)]">
                    <motion.img
                      variants={vImageZoom}
                      src="https://mhcglobal.info/Media/Home/aircraft-simulator.webp"
                      alt="Student pilot training on aircraft flight simulator with dual screen cockpit"
                      title="Flight Simulator Training – MH Cockpit Aviation Academy"
                      className="w-full h-[300px] sm:h-[420px] md:h-[520px] lg:h-[620px] object-cover"
                      loading="lazy"
                      draggable="false"
                    />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <ImpactStatsGallerySection />
        <DiversifiedCommunityText />

        {/* CTA FOOTER */}
        <footer className="w-full">
          <div className="bg-white border-t border-black/20">
            <div className="max-w-[1800px] mx-auto px-4 sm:px-6 md:px-8 lg:px-8 py-6 sm:py-8">
              <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(320px,560px)] items-center gap-6 sm:gap-8 lg:gap-10">
                <motion.div
                  className="text-center xl:text-left"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                >
                  <h2 className="text-[34px] sm:text-[52px] md:text-[66px] lg:text-[82px] xl:text-[100px] font-bold leading-[0.95] tracking-[-0.04em] text-black">
                    Grow Today with MH
                  </h2>
                </motion.div>

                <motion.div
                  className="flex justify-center xl:justify-end"
                  initial={{ opacity: 0, x: 40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.8, ease: "easeOut", delay: 0.1 }}
                >
                  <a
                    href={joinLink}
                    target="_blank"
                    rel="noreferrer"
                    className="w-full max-w-[560px]
                               h-[78px] sm:h-[96px] md:h-[120px] lg:h-[150px] xl:h-[190px]
                               bg-[#2FA2D6] rounded-[10px]
                               flex items-center justify-center text-center
                               px-4 sm:px-6
                               text-white text-[18px] sm:text-[22px] md:text-[26px] lg:text-[30px]
                               font-semibold tracking-[0.01em]
                               hover:brightness-105 transition"
                  >
                    JOIN MH COCKPIT
                  </a>
                </motion.div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </section>
  );
}